import { get, noop, isEmpty } from 'lodash'
import fetch from '@adc/fetch'
import configment from '@adc/configment'
import { utils, DATA_TYPES } from '@adc/ui-morpheus'
import { constraints } from '@adc/constants'
import seek, { seekTypes } from '@adc/seek'
import { gql, utils as sdkUtils } from '@adc/sdk'
import _clamp from 'lodash/clamp'
import {
  deleteAllRestrictions,
  getRestrictions,
  updateEditRestrictions,
  updateProductRestrictions,
} from './restrictions'

const { BACKEND_MAX_PAGE_SIZE } = constraints

const formatFunds = data => ({
  max_bid_fund_amount: get(data, 'max_bid_fund_amount', null),
  max_bid_fund_status: get(data, 'max_bid_fund_status', null),
  party_max_bid_id: get(data, 'party_max_bid_id', null),
})

const getPartyPerson = async (req, id) => {
  const filterBy = [
    { dataType: DATA_TYPES.NUMBER, fields: ['user_id'], values: [id] },
  ]
  const query = utils.elasticSearch.buildQuery(filterBy)
  const res = await fetch(
    `${configment.get('CS_PARTYSEARCH')}/partysearch/v1/parties/person/dsl`,
    {
      req,
      method: 'POST',
      body: {
        query,
        sort: [],
        offset: 0,
        size: 1,
      },
    }
  )

  const partyPerson = res.body.content[0]

  // get party roles from service (not partysearch, as it's off indexing after we update)
  const partyId = get(partyPerson, 'person.party_id')
  if (partyId) {
    const partyRoles = await getPartyRoles(req, partyPerson.person.party_id)
    partyPerson.roles = partyRoles
  }
  return partyPerson
}

const getPartyFunds = async (req, partyId) => {
  const res = await fetch(
    `${configment.get(
      'RESI_MS_PROFILE'
    )}/resi/profile/v1/max_bid/${partyId}/fund`,
    { req }
  ).catch(e => {
    if (e.status === 404) {
      return e
    } else {
      throw e
    }
  })
  return res.body
}

const getUserOnlineRegistrations = async (
  partyId,
  page = 0,
  size = BACKEND_MAX_PAGE_SIZE
) => {
  if (!partyId) {
    return []
  }
  const res = await fetch
    .fromService('RESI_MS_PROFILE')
    .get(`/resi/profile/v1/parties/${partyId}/online-bidder-registrations`, {
      queryParams: {
        page,
        size: _clamp(size, 1, BACKEND_MAX_PAGE_SIZE),
      },
    })

  return res?.body
}

const getUserPropertiesBannedFromBidding = async (
  partyId,
  page = 0,
  size = BACKEND_MAX_PAGE_SIZE
) => {
  if (!partyId) {
    return []
  }
  const res = await fetch
    .fromService('RESI_MS_ONLINE_AUCTION')
    .get(`/resi/online-auction/v1/parties/${partyId}/online/auction-bans`, {
      queryParams: {
        page,
        size: _clamp(size, 1, BACKEND_MAX_PAGE_SIZE),
      },
    })

  return res?.body
}

const unbanUserFromProperty = async (propertyId, partyId) => {
  if (!partyId || !propertyId) {
    throw new Error('unbanUserFromProperty: Missing partyId or property')
  }
  await fetch
    .fromService('RESI_MS_ONLINE_AUCTION')
    .delete(
      `/resi/online-auction/v1/properties/${propertyId}/parties/${partyId}/online/auction-bans`
    )
}

const deleteRegisteredUserListing = async ({ listingId, partyId }) => {
  const res = await fetch
    .fromService('RESI_MS_PROFILE')
    .delete(
      `/resi/profile/v1/listings/${listingId}/online-bidder-registrations/parties/${partyId}`
    )

  return res.body
}

const getPartyBidDeposit = (req, party_id) =>
  fetch
    .fromService('RESI_MS_PAYMENT')
    .get(`/resi/payment/v1/parties/${party_id}/party-bid-deposits`)
    .then(res => res.body)
    .catch(e => ({ is_bid_deposit_active: false }))

const upsertPartyBidDeposit = (req, party_id, party_bid_deposit) =>
  fetch
    .fromService('RESI_MS_PAYMENT')
    .post(`/resi/payment/v1/parties/${party_id}/party-bid-deposits`, {
      body: { party_id, ...party_bid_deposit },
    })
    .then(res => res.body)

const getListings = ({
  listingIds = [],
  fields = [],
  sortBy = {},
  paginationState = {},
  fieldsConfig = undefined,
}) =>
  seek
    .for(seekTypes.LISTINGS)
    .fields([...fields], fieldsConfig)
    .dsl({
      equals: {
        fields: 'listing_id.keyword',
        values: [...listingIds],
      },
    })
    .sortByField(sortBy?.field, sortBy?.order)
    .size(paginationState?.pageSize)
    .offset(paginationState?.offset)
    .execute()

const getListingsByPropertyIds = (propertyIds = [], fields = [], sortBy) =>
  seek
    .for(seekTypes.ACTIVE_LISTINGS)
    .fields([...fields])
    .dsl({
      equals: {
        fields: 'properties.property_id.keyword',
        values: [...propertyIds],
      },
    })
    .sortByField(sortBy?.field, sortBy?.order)
    .dangerouslyExecuteAll()

// TODO: will probably need to include that in providers/graphUser:loadUserPageData eventually
const loadBidderSignature = async (userId, refreshCache = false) => {
  const cachePolicy = refreshCache ? 'ORIGIN_ONLY' : 'CACHE_ONLY'
  const res = await fetch.forAuctionGraph({
    query: gql`
      query resiFeBtbHome_bidderSignature(
        $userId: ID!
        $cachePolicy: ResolvePolicy
      ) {
        user(userId: $userId) {
          # This is a shadow read because we implemented a pattern whereby these are backfilled on read
          # if they don't already exist. Doing here to get that started naturally.
          bidder_number

          bidder_signature(resolvePolicy: $cachePolicy) {
            __typename

            ... on OnlineBidderSignature {
              _alias_online__system_derived_persona: system_derived_persona
              _alias_online__staff_derived_persona: staff_derived_persona

              historical {
                buyer_caused_fallout_count
                bidder_suspension_count
                bidder_ip_address_suspension_count
                auctions_with_bids_count
                auctioneer_deleted_bids_count
                auctions_quality_bids_count
                winning_bids_count
                auction_registration_count
                average_bids_count_per_auction
                average_quality_bids_count_per_auction
                contracts_closed_count
                latest_bid_date
                latest_purchase_date
                latest_registration_date
              }
              current {
                is_counter_bidding_against_nobody
                auction_registration_count
                auctions_with_bids_count
                auctions_with_quality_bids_count
                is_first_time_bidder
                contracts_in_progress_count
                remaining_registration_count
              }
            }

            ... on LiveBidderSignature {
              historical {
                buyer_caused_fallout_count
                bidder_suspension_count
                bidder_ip_address_suspension_count
                auctions_with_bids_count
                auctioneer_deleted_bids_count
                auctions_quality_bids_count
                winning_bids_count
                auction_registration_count
                average_bids_count_per_auction
                average_quality_bids_count_per_auction
                contracts_closed_count
                latest_bid_date
                latest_purchase_date
                latest_registration_date
              }
              current {
                is_counter_bidding_against_nobody
                auction_registration_count
                auctions_with_bids_count
                auctions_with_quality_bids_count
                is_first_time_bidder
                contracts_in_progress_count
              }
            }

            ... on ProfileBidderSignature {
              current {
                saved_properties_count
                saved_searches_count
              }
            }

            ... on UaaBidderSignature {
              current {
                user_created_date
              }
            }

            ... on SellerSignature {
              _alias_seller__system_derived_persona: system_derived_persona
              _alias_seller__staff_derived_persona: staff_derived_persona

              current {
                active_listing_count
              }
            }
          }
        }
      }
    `,
    variables: {
      userId,
      cachePolicy,
    },
  })

  return sdkUtils.normalizeAliases(res.body)
}

const PartyWalletTransactionLogDefaultPageSize = 10

// eslint-disable-next-line @adc/named-gql-operations
export const WALLET_FRAGMENT = gql`
  fragment Wallet on User {
    user_id
    party_id
    wallet(refreshWallet: true) {
      party_id
      aggregations {
        consumable_fund_amount
        payment_type
        fund_reconciliation_status
        updated_date
      }
      deposit_tier {
        deposit_amount
        buyer_caused_fallout_after_latest_close_count
        close_after_latest_buyer_caused_fallout_count
        persona_type
      }
      transaction_log_history(filters: $transactionLogsFilters) {
        current_page
        total_count
        total_pages
        content {
          id
          party_id
          transaction_parent_id
          user {
            user_id
            email
          }
          transaction_status
          transaction_amount
          transaction_type
          transaction_log_type
          charge_reason
          failure_reason
          payment_method_type
          payment_type
          created_date
          public_id
          external_id
          settlement_date
          charge_reason
          context_identifiers {
            id
            transaction_log_id
            context_source
            action
            resolved_context {
              __typename

              ... on Listing {
                ...TransactionLogListing
              }

              ... on LiveAuctionSegment {
                highest_bid_type
                highest_bid_amount
                configuration {
                  state_deposit_rule
                }
                listing {
                  ...TransactionLogListing
                }
              }

              ... on OnlineAuctionSegment {
                listing {
                  ...TransactionLogListing
                }
              }
            }
          }
        }
      }
    }
  }

  fragment TransactionLogListing on Listing {
    listing_id
    listing_page_path
    formatted_address(format: SINGLE_LINE)
  }
`
export const fetchUserWallet = async (
  userId,
  transactionLogsFilters = {
    take: PartyWalletTransactionLogDefaultPageSize,
    page: 0,
  }
) => {
  return fetch
    .forAuctionGraph({
      // eslint-disable-next-line @adc/named-gql-operations
      query: gql`
        ${WALLET_FRAGMENT}
        query resiFeBtbHome_UserPartyWallet(
          $userId: ID!
          $transactionLogsFilters: PartyTransactionHistoryEntryFilters
        ) {
          user(userId: $userId) {
            ...Wallet
          }
        }
      `,
      variables: {
        userId,
        transactionLogsFilters: {
          should_apply_collapse_aggregation: true,
          ...transactionLogsFilters,
        },
      },
    })
    .then(({ body }) => body)
}

export const fetchUserDocuments = async (
  userId,
  transactionLogsFilters = {
    take: PartyWalletTransactionLogDefaultPageSize,
    page: 0,
  }
) => {
  return fetch
    .forAuctionGraph({
      query: gql`
        query resiFeBtbHome_UserDocuments($userId: ID!) {
          user(userId: $userId) {
            associated_user_documents {
              document_purpose_code
              urn
              user_document_id
            }
          }
        }
      `,
      variables: {
        userId,
      },
    })
    .then(({ body }) => body)
}
const getUser = async (req, id) => {
  const partyPerson = await getPartyPerson(req, id)
  const partyId = get(partyPerson, 'person.party_id')
  const userId = get(partyPerson, 'user_id')
  const fulfilledStatus = 'fulfilled'

  const [
    verificationStatus,
    partyFunds,
    partyBidDeposit,
    restrictions,
    bidderSignature,
    bidderWallet,
    userDocuments,
  ] = await Promise.allSettled([
    getVerificationStatus(req, partyId),
    getPartyFunds(req, partyId),
    getPartyBidDeposit(req, partyId),
    getRestrictions(id),
    loadBidderSignature(userId),
    fetchUserWallet(userId),
    fetchUserDocuments(userId),
  ])

  return {
    ...partyPerson,
    ...(bidderSignature.status === fulfilledStatus &&
      bidderSignature.value.data.user),
    verification_status:
      verificationStatus.status === fulfilledStatus && verificationStatus.value,
    max_bid_settings: formatFunds(
      verificationStatus.status === fulfilledStatus && partyFunds.value
    ),
    restrictions: restrictions.status === fulfilledStatus && restrictions.value,
    party_bid_deposit: partyBidDeposit.value,
    ...(bidderWallet.status === fulfilledStatus &&
      bidderWallet.value.data.user),
    associated_user_documents:
      (userDocuments.status === fulfilledStatus &&
        userDocuments.value?.data?.user?.associated_user_documents) ||
      [], // default to empty array as better that one part of UI doesn't work than whole thing breaks if we get weird response
  }
}

const getVerificationStatus = async (req, partyId) => {
  const res = await fetch(
    `${configment.get(
      'RESI_MS_PROFILE'
    )}/resi/profile/v1/id-verification/${partyId}`,
    {
      req,
    }
  )
  return res.body
}

const getUsers = async (req, fetchOptions) => {
  const { offset, size, sortBy, filterBy } = fetchOptions
  const query = utils.elasticSearch.buildQuery(filterBy)

  const res = await fetch(
    `${configment.get('CS_PARTYSEARCH')}/partysearch/v1/parties/person/dsl`,
    {
      req,
      method: 'POST',
      body: {
        query,
        sort: sortBy,
        offset,
        size,
      },
    }
  )
  return res.body
}

const putUserRoles = async (req, userId, roles) => {
  // note - for some reason this API does not return what it just updated, but instead empty body
  await fetch(`${configment.get('MS_UAA')}/uaa/v1/users/${userId}/roles`, {
    req,
    method: 'PUT',
    body: {
      roles,
    },
  })
}

const getPartyRoles = async (req, partyId) => {
  const res = await fetch(
    `${configment.get('MS_UAA')}/uaa/v1/parties/${partyId}/roles`,
    { req }
  )
  return res.body
}

const putPartyRoles = async (req, partyId, roles) => {
  // note - for some reason this API does not return what it just updated, but instead empty body
  await fetch(`${configment.get('MS_UAA')}/uaa/v1/parties/${partyId}/roles`, {
    req,
    method: 'PUT',
    body: roles,
  })
}

const putPerson = async (req, data) => {
  const res = await fetch(
    `${configment.get('MS_UAA')}/uaa/v1/party/person/details`,
    {
      req,
      method: 'PUT',
      body: data,
    }
  )

  return {
    security: data.security,
    user_id: data.user_id,
    ...res.body.entity,
  }
}

const putPartyFunds = async (req, data) => {
  const partyId = get(data, 'person.party_id')
  const res = await fetch(
    `${configment.get(
      'RESI_MS_PROFILE'
    )}/resi/profile/v1/max_bid/${partyId}/fund`,
    {
      req,
      method: 'PUT',
      body: {
        party_id: partyId,
        ...data.max_bid_settings,
      },
    }
  )
  return res.body
}

const verificationOverwrite = async (req, data) => {
  const partyId = get(data, 'person.party_id')
  const res = await fetch(
    `${configment.get(
      'RESI_MS_PROFILE'
    )}/resi/profile/v1/id-verification/override`,
    {
      req,
      method: 'PUT',
      body: {
        party_id: partyId,
        internal_comments: 'Morpheus Update',
        ...data.verification_status,
      },
    }
  )
  return res.body
}
const verificationDeleteUnsuccessfulAttempts = async (req, partyId) => {
  const res = await fetch(
    `${configment.get(
      'RESI_MS_PROFILE'
    )}/resi/profile/v1/id-verification/delete-unsuccessful-attempts/${partyId}`,
    {
      req,
      method: 'POST',
    }
  )
  return res.body
}

const postPartyFunds = async (req, data) => {
  const partyId = get(data, 'person.party_id')
  const res = await fetch(
    `${configment.get('RESI_MS_PROFILE')}/resi/profile/v1/max_bid/fund`,
    {
      req,
      method: 'POST',
      body: {
        party_id: partyId,
        ...data.max_bid_settings,
      },
    }
  )
  return res.body
}

const putPersonData = async (req, data, changeVerificationStatus) => {
  const { party_bid_deposit: updatedBidDeposit } = data

  let partyFundsMethod = noop
  if (data.max_bid_settings.party_max_bid_id) {
    partyFundsMethod = putPartyFunds
  } else if (data.max_bid_settings.max_bid_fund_status) {
    partyFundsMethod = postPartyFunds
  }

  const [person, funds, restrictions, party_bid_deposit, verification_status] =
    await Promise.all([
      putPerson(req, data),
      partyFundsMethod(req, data),
      updateUserRestrictions(data.user_id, data.restrictions),
      upsertPartyBidDeposit(req, data.person.party_id, updatedBidDeposit),
      ...(changeVerificationStatus ? [verificationOverwrite(req, data)] : []),
    ])

  return {
    ...person,
    verification_status: verification_status || data.verification_status,
    max_bid_settings: formatFunds(funds),
    party_bid_deposit,
    restrictions,
  }
}

const putUser = async (req, data, changeVerificationStatus) => {
  if (Array.isArray(data.security.roles)) {
    // order matters for some reason with the two PUTs
    await putUserRoles(null, data.user_id, data.security.roles)
  }

  if (Array.isArray(data.roles)) {
    await putPartyRoles(null, data.person.party_id, data.roles)
  }

  return putPersonData(req, data, changeVerificationStatus)
}

const searchUsers = (req, userIds) => {
  const uniqIds = new Set(userIds)

  return fetch
    .fromService('MS_UAA')
    .post('/uaa/v1/users/search', {
      queryParams: {
        size: uniqIds.size,
      },
      body: {
        user_id_list: Array.from(uniqIds),
      },
      req,
    })
    .then(res => res.body)
}

const resendConfirmationEmail = (req, email) =>
  fetch.fromService('MS_UAA').post('/uaa/v1/users/resend_activation', {
    body: {
      username: email,
    },
  })

const updateUserSecurityStatus = (req, email, status) =>
  fetch.fromService('MS_UAA').put('/uaa/v1/users/status', {
    body: {
      username: email,
      status,
    },
  })

const updateUserRestrictions = async (userId, restrictions = []) => {
  let res = []
  if (isEmpty(restrictions)) {
    await deleteAllRestrictions(userId)
  } else {
    const [productRestrictions, editRestrictions] = await Promise.all([
      updateEditRestrictions(userId),
      updateProductRestrictions(userId, restrictions),
    ])
    res = [...productRestrictions, ...editRestrictions]
  }
  return res
}

const postForceSendUpdateMessages = async userId =>
  fetch
    .fromService('MS_UAA')
    .post(`/uaa/v1/users/_force-send-update-messages`, {
      body: [userId],
    })

const updatePartyFunds = async ({
  partyId,
  amount,
  type,
  reason,
  referenceId,
  context_source,
}) => {
  let query = ''
  const contextIdentifier = { id: referenceId, context_source, action: reason }
  const AddMoneyMutation = `
        mutation morpheusAddMoneyToFund($partyId: Int!, $amount: Float!, $contextIdentifier: TransactionContextIdentifierInput) {
          addMoneyToFund(partyId: $partyId, amount: $amount, contextIdentifier: $contextIdentifier) {
            fund_amount
            fund_status
          }
        }
      `

  const RemoveMoneyMutation = `
        mutation morpheusSubtractMoneyToFund($partyId: Int!, $amount: Float!, $contextIdentifier: TransactionContextIdentifierInput) {
          subtractMoneyFromFund(partyId: $partyId, amount: $amount, contextIdentifier: $contextIdentifier) {
            fund_amount
            fund_status
          }
        }
      `
  if (type === 'credit') {
    query = AddMoneyMutation
  } else if (type === 'debit') {
    query = RemoveMoneyMutation
  } else {
    throw new Error('Invalid transaction type')
  }

  const variables = {
    partyId,
    amount,
    contextIdentifier,
  }
  return fetch.forAuctionGraph({ query, variables })
}

/**
 * Append a wallet transaction (unified CREDIT/DEBIT) via ms-payment.
 */
const appendWalletTransaction = async ({
  partyId,
  paymentType,
  transactionType,
  amount,
  chargeReason,
  contextIdentifiers,
}) => {
  const query = `
        mutation morpheusAppendWalletTransaction($input: AppendWalletTransactionInput!) {
          appendWalletTransaction(input: $input) {
            party_id
            aggregations {
              payment_type
              consumable_fund_amount
            }
          }
        }
      `
  const variables = {
    input: {
      partyId,
      paymentType,
      transactionType,
      amount,
      ...(chargeReason ? { chargeReason } : {}),
      contextIdentifiers: contextIdentifiers || [],
    },
  }
  return fetch.forAuctionGraph({ query, variables })
}

/**
 * Fetch an array of users based on an email filter a query that looks like
 */
const fetchUsersByEmail = (emailQueryString, limit) =>
  fetch
    .forAuctionGraph({
      query: gql`
        query resiFeBtbHome_GetUsersByEmail($email: String!) {
          users(filter: { email: $email }) {
            user_id
            email
            party_id
          }
        }
      `,
      variables: {
        email: emailQueryString,
      },
    })
    .then(res => {
      let users = res.body.data.users || []

      // Add fake system user if search matches "system" or "system.auction"
      if (
        emailQueryString === 'system' ||
        emailQueryString === 'system.auction'
      ) {
        users = [...users, { user_id: '0', email: 'system.auction' }]
      }

      if (limit) {
        users = users.slice(0, limit)
      }
      return users
    })

const getEmailByUserId = async userId => {
  // Handle special case for system user
  if (parseInt(userId, 10) === 0) {
    return 'system.auction'
  }
  const res = await fetch.forAuctionGraph({
    query: gql`
      query resiFeBtbHome_getEmailByUserId($userId: ID!) {
        user(userId: $userId) {
          email
        }
      }
    `,
    variables: {
      userId,
    },
  })
  return res.body.data.user?.email || null
}

const getEmailByPartyId = async partyId => {
  // Handle special case for system user
  if (parseInt(partyId, 10) === 0) {
    return 'system.auction'
  }
  const res = await fetch.forAuctionGraph({
    query: gql`
      query resiFeBtbHome_getEmailByPartyId($partyId: ID!) {
        user_from_party(partyId: $partyId) {
          email
        }
      }
    `,
    variables: {
      partyId,
    },
  })
  return res.body.data.user_from_party?.email || null
}

const updateUserPersonaType = async (partyId, personaType) => {
  return fetch.forAuctionGraph({
    query: gql`
      mutation resiFeBtbHome_UpdateUserPersonaType(
        $input: UpdateUserPersonaTypeInput!
      ) {
        updateUserPersonaType(input: $input) {
          bidder_party_id
          staff_derived_persona
          system_derived_persona
        }
      }
    `,
    variables: {
      input: { party_id: partyId, persona_type: personaType },
    },
  })
}

export {
  getUser,
  getUsers,
  putUserRoles,
  putPersonData,
  putUser,
  searchUsers,
  verificationDeleteUnsuccessfulAttempts,
  resendConfirmationEmail,
  updateUserSecurityStatus,
  getUserOnlineRegistrations,
  getUserPropertiesBannedFromBidding,
  unbanUserFromProperty,
  getListings,
  getListingsByPropertyIds,
  updatePartyFunds,
  appendWalletTransaction,
  deleteRegisteredUserListing,
  postForceSendUpdateMessages,
  loadBidderSignature,
  PartyWalletTransactionLogDefaultPageSize,
  fetchUsersByEmail,
  getEmailByUserId,
  getEmailByPartyId,
  updateUserPersonaType,
}
